import { fetchWrapper } from '~/utils/fetchWrapper'

export interface DomainCheckResponse {
  configured: boolean
  status: string
  setup_required?: boolean
  tenant_id?: string
  cognito_domain?: string
  hosted_ui_url?: string
  provider_type?: string
  domain: string
  error?: string
}

export interface TenantSetupRequest {
  domain: string
  provider_type: string
  org_name?: string
  // Microsoft provider config
  microsoft_client_id?: string
  microsoft_client_secret?: string
  microsoft_tenant_id?: string
  // Okta provider config
  okta_domain?: string
  okta_client_id?: string
  okta_client_secret?: string
  // Google provider config
  google_client_id?: string
  google_client_secret?: string
  // SAML provider config
  saml_metadata_url?: string
  saml_provider_name?: string
}

export interface TenantSetupResponse {
  success: boolean
  tenant_id?: string
  domain?: string
  status?: string
  verification_token?: string
  cognito_config?: {
    user_pool_id: string
    app_client_id: string
    cognito_domain: string
    hosted_ui_url: string
    idp_redirect_uri: string
  }
  provider_info?: Record<string, any>
  next_steps?: string[]
  error?: string
}

export interface TenantVerificationRequest {
  domain: string
  verification_token: string
}

export interface TenantVerificationResponse {
  success: boolean
  status?: string
  domain?: string
  tenant_id?: string
  hosted_ui_url?: string
  error?: string
}

export interface SSOLoginResponse {
  success: boolean
  login_url?: string
  tenant_id?: string
  domain?: string
  provider_type?: string
  state?: string
  error?: string
}

// List of public email domain providers that should not show SSO options
const PUBLIC_EMAIL_DOMAINS = new Set([
  // Google domains
  'gmail.com',
  'googlemail.com',

  // Microsoft domains
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',

  // Yahoo domains
  'yahoo.com',
  'ymail.com',
  'yahoo.co.uk',
  'yahoo.fr',
  'yahoo.de',

  // Apple domains
  'icloud.com',
  'me.com',
  'mac.com',

  // Other popular providers
  'aol.com',
  'protonmail.com',
  'pm.me',
  'tutanota.com',
  'zoho.com',
  'mail.com',
  'gmx.com',
  'gmx.de',
  'web.de',
  't-online.de',
  'freenet.de',
  'yandex.com',
  'yandex.ru',
  'mail.ru',
  'rambler.ru',

  // Additional domains
  'qq.com',
  '163.com',
  '126.com',
  'sina.com',
  'sohu.com',
  'naver.com',
  'daum.net',
  'nate.com'
])

/**
 * Check if a domain is a public email provider
 */
export const isPublicEmailDomain = (domain: string): boolean => {
  return PUBLIC_EMAIL_DOMAINS.has(domain.toLowerCase())
}

/**
 * Extract domain from email address
 */
export const extractDomainFromEmail = (email: string): string => {
  if (!email || !email.includes('@')) {
    throw new Error('Invalid email format: missing @ symbol')
  }

  const parts = email.split('@')
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    throw new Error('Invalid email format: malformed email address')
  }

  const domain = parts[1].toLowerCase().trim()
  if (!domain) {
    throw new Error('Invalid email format: empty domain')
  }

  return domain
}

/**
 * Check if domain is configured for SSO
 */
export const checkDomainConfiguration = async (domain: string): Promise<DomainCheckResponse> => {
  return await fetchWrapper.get<DomainCheckResponse>(
    `/api/tenants/check/${encodeURIComponent(domain)}`
  )
}

/**
 * Setup tenant configuration
 */
export const setupTenant = async (request: TenantSetupRequest): Promise<TenantSetupResponse> => {
  return await fetchWrapper.post<TenantSetupResponse>('/api/tenants/setup', request)
}

/**
 * Verify tenant configuration
 */
export const verifyTenant = async (
  domain: string,
  request: TenantVerificationRequest
): Promise<TenantVerificationResponse> => {
  return await fetchWrapper.post<TenantVerificationResponse>(
    `/api/tenants/verify/${encodeURIComponent(domain)}`,
    request
  )
}

/**
 * Get SSO login URL for email address.
 * In plugin mode, pass pluginState + redirectBaseUrl so the SSO callback
 * can route back through the localhost auth-callback server.
 */
export const getSSOLoginUrl = async (
  email: string,
  options?: { pluginState?: string; redirectBaseUrl?: string }
): Promise<SSOLoginResponse> => {
  const body: Record<string, unknown> = { email }
  if (options?.pluginState) {
    body.plugin_state = options.pluginState
    body.redirect_base_url = options.redirectBaseUrl
  }
  return await fetchWrapper.post<SSOLoginResponse>('/api/tenants/sso-login', body)
}
